import { ArrivalInput, DepartInput, followDate, moveReturnToShuttle } from '../components/ArrivalInput';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { api, type DisptInput } from '../lib/api';
import { ErrorBox, money, MoneyInput } from '../components/DataTable';
import { vatSplit, VatToggle } from '../components/Vat';
import { RecvFields, toRecvInput, type RecvDraft } from '../components/RecvFields';
import { CarKindSelect, CarKindSettingsButton, CarYearSelect } from '../components/CarKindPicker';

/**
 * 신규 접수 — **접수 + 노선 여러 줄을 한 화면에서** 넣는다.
 *
 * 왜 이렇게 바꿨나
 *   전에는 접수를 저장하면 화면이 닫히고, 목록에서 그 접수를 다시 찾아 "노선 추가"를
 *   노선 수만큼 눌러야 했다. 노선 5개면 왕복 5번이고, 중간에 그만두면 **노선 없는 접수**가 남는다.
 *   서버가 한 트랜잭션으로 넣으므로(`createRecvWithRoutes`) 전부 서거나 전부 없다.
 *
 * ⚠️ **같은 노선에 여러 대가 들어가면 줄을 늘리는 게 아니라 “대수”를 올린다.**
 *   `cost_cust` 는 **노선 청구 총액**, `cost_driver` 는 **1대당 단가**다(CLAUDE.md 불변식 · issues 179 —
 *   레거시 청구액은 대수와 함께 커지는 총액이고 청구 집계도 대수를 곱하지 않는다). 3대짜리를 3줄로 넣으면
 *   청구가 3배가 된다. 서버는 두 경우를 구분할 수 없다 — 그래서 줄마다
 *   운행료 `단가 × 대수 = 합계`를 같은 줄에 찍어 입력하는 사람이 그 자리에서 알아채게 한다.
 *
 * ⚠️ 왕복도 줄을 늘리지 않는다 — `왕복` 을 켜면 복편 시각 두 칸이 그 줄에 생긴다.
 *   (`is_shuttle` 은 통근이 아니라 **왕복**이다. 편도면 서버가 복편 시각을 NULL 로 강제한다.)
 */

type RouteRow = DisptInput & {
  /** 화면 전용 — 서버로 보내지 않는다. */
  _key: number;
  _open: boolean;
  /** 정산월을 사람이 직접 고쳤나. 안 고쳤으면 출발일시에서 계속 따라온다. */
  _monTouched: boolean;
  /**
   * #512 — 「운행료 합계」 칸에 직접 친 값(화면 전용). 저장은 여전히 1대당 단가(`costDriver`)만 —
   * 합계 ÷ 대수를 단가로 넣는다. 단가·대수를 다시 고치면 비워서 합계가 계산값으로 돌아간다.
   */
  _drvTotal?: string;
};

let seq = 0;
const newRow = (carKind?: string): RouteRow => ({
  _key: ++seq,
  _open: false,
  _monTouched: false,
  carCnt: 1,
  isShuttle: false,
  carKind: carKind ?? '',
});

/** 표시용 합계. 저장 값은 문자열 그대로 보낸다 — 숫자로 왕복시키면 소수 4자리가 깎인다. */
function lineTotal(unit: string | undefined, cnt: number | undefined): number | null {
  if (unit === undefined || unit.trim() === '') return null;
  const n = Number(unit.replace(/,/g, ''));
  if (!Number.isFinite(n)) return null;
  return n * (cnt && cnt > 0 ? cnt : 1);
}

/**
 * #512 「운행료 합계」 칸에 친 합계 → 1대당 단가. 대수로 나누어떨어지지 않으면 원 단위로 반올림한다
 * (그 경우 칸 아래에 실제 저장될 「단가 × 대수」를 같이 보여 준다).
 */
function unitFromTotal(total: string, cnt: number | undefined): string {
  const t = total.trim();
  if (t === '' || t === '-') return t;
  const n = Number(t);
  if (!Number.isFinite(n)) return t;
  const c = cnt && cnt > 0 ? cnt : 1;
  return String(Math.round(n / c));
}

export default function RecvIntakeForm({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  const [d, setD] = useState<RecvDraft>({ recvType: '일반', isBidding: false, isMeal: false, isLodge: false });
  const [rows, setRows] = useState<RouteRow[]>([newRow()]);
  /** 청구액을 부가세포함가로 적나(#87) — 거래처 단위로 정하는 값이라 접수 전체에 한 번. */
  const [vatIncl, setVatIncl] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (patch: Partial<RecvDraft>) => setD((cur) => ({ ...cur, ...patch }));

  const setRow = (key: number, patch: Partial<RouteRow>) =>
    setRows((cur) => cur.map((r) => (r._key === key ? { ...r, ...patch } : r)));

  /** 출발일시를 넣으면 정산월이 따라온다 — 사람이 직접 고친 줄은 건드리지 않는다. */
  function setStart(key: number, startTime: string) {
    setRows((cur) =>
      cur.map((r) => {
        if (r._key !== key) return r;
        const mon = startTime.slice(0, 7);
        const endTime = followDate(r.startTime, startTime, r.endTime) ?? r.endTime;
        return { ...r, startTime, endTime, driveMon: r._monTouched ? r.driveMon : mon };
      }),
    );
  }

  /**
   * #414 — 거래처를 고르면 그 거래처가 지난 노선에 쓴 출발지를 빈 출발지 칸에 채운다(나머지는 고르기 목록).
   * 거래처를 바꾸면 앞 거래처 값으로 채워 둔 칸만 새 값으로 바꾼다 — 사람이 친 출발지는 건드리지 않는다.
   */
  const [startLocs, setStartLocs] = useState<string[]>([]);
  const autoLoc = useRef('');
  useEffect(() => {
    let alive = true;
    const custId = d.custId;
    if (!custId) { setStartLocs([]); return; }
    api.listCustStartLocs({ custId })
      .then((r) => {
        if (!alive) return;
        setStartLocs(r.locs);
        const prev = autoLoc.current;
        const next = r.locs[0] ?? '';
        autoLoc.current = next;
        setRows((cur) => cur.map((row) => {
          const v = (row.startLoc ?? '').trim();
          return v === '' || (prev !== '' && v === prev) ? { ...row, startLoc: next } : row;
        }));
      })
      .catch(() => { if (alive) setStartLocs([]); });
    return () => { alive = false; };
  }, [d.custId]);

  const addRow = () => setRows((cur) => [...cur, { ...newRow(cur[cur.length - 1]?.carKind), startLoc: autoLoc.current }]);

  const dupRow = (key: number) =>
    setRows((cur) => {
      const i = cur.findIndex((r) => r._key === key);
      if (i < 0) return cur;
      const src = cur[i]!;
      const copy: RouteRow = { ...src, _key: ++seq, _open: false };
      return [...cur.slice(0, i + 1), copy, ...cur.slice(i + 1)];
    });

  const delRow = (key: number) => setRows((cur) => (cur.length === 1 ? cur : cur.filter((r) => r._key !== key)));

  /** #260 — 아무것도 안 적은 줄(줄 추가만 누른 것)은 저장에서 뺀다. 그 줄 때문에 접수가 막히지 않게. */
  const isBlank = (r: RouteRow) =>
    ![r.disptName, r.startLoc, r.endLoc, r.startTime, r.endTime, r.costCust, r.costDriver]
      .some((v) => (v ?? '').toString().trim() !== '');
  const filled = rows.filter((r) => !isBlank(r));
  const toSave = filled.length > 0 ? filled : rows;

  const totals = useMemo(() => {
    let cust = 0;
    let driver = 0;
    for (const r of rows) {
      // 청구는 노선 총액이라 대수를 곱하지 않는다(1). 운행료만 1대당 × 대수.
      cust += lineTotal(r.costCust, 1) ?? 0;
      driver += lineTotal(r.costDriver, r.carCnt) ?? 0;
    }
    return { cust, driver };
  }, [rows]);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const header = toRecvInput(d);
      const routes: DisptInput[] = toSave.map(({ _key, _open, _monTouched, _drvTotal, ...route }) => {
        void _key;
        void _open;
        void _monTouched;
        void _drvTotal;
        return { ...route, vatIncl };
      });
      const r = await api.createRecvWithRoutes({ ...header, routes });
      onSaved(`접수 ${r.recvId}번과 노선 ${r.disptIds.length}건을 등록했습니다 (${r.custName ?? ''}).`);
    } catch (e) {
      // 서버가 몇 번째 노선이 왜 막혔는지 문장으로 준다. 그대로 보여준다.
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  }

  return (
    <div className="card space-y-5 p-5">
      <div className="flex items-center gap-3">
        <h2 className="text-base font-bold">신규 접수</h2>
        <span className="text-xs text-ink-faint">접수일은 등록일로 자동 기록됩니다</span>
        <button className="btn-ghost ml-auto" onClick={onClose} disabled={saving}>
          취소
        </button>
        <button className="btn-primary" onClick={save} disabled={saving || (!d.custId && !(d.newCust?.custName?.trim() && d.newCust?.bizNumber?.trim()))}>
          {saving ? '저장 중…' : `접수 + 노선 ${toSave.length}건 저장`}
        </button>
      </div>

      {error && <ErrorBox message={error} />}

      <RecvFields d={d} set={set} />

      <div className="border-t border-line pt-4">
        <div className="mb-2 flex items-center gap-3">
          <h3 className="text-sm font-bold">노선 {rows.length}건</h3>
          <VatToggle value={vatIncl} onChange={setVatIncl} disabled={saving} />
          <p className="text-xs text-ink-faint">
            같은 노선에 여러 대가 들어가면 줄을 늘리지 말고 <b>대수</b>를 올리세요 — <b>청구는 노선 총액</b>,{' '}
            <b>운행료는 1대당</b>입니다.
          </p>
          <span className="ml-auto" />
          <CarKindSettingsButton />
          <button type="button" className="btn-ghost py-1 text-xs" onClick={addRow} disabled={saving}>
            + 줄 추가
          </button>
        </div>

        {/* 칸이 많아 좁은 화면에서는 표만 가로로 스크롤한다(페이지 본문은 안 밀린다). */}
        <div className="overflow-x-auto rounded-lg border border-line">
          <table className="w-full min-w-[135.5rem] table-fixed text-sm">
            <thead className="bg-canvas text-xs text-ink-soft">
              <tr>
                <Th className="w-8">#</Th>
                {/* #260 — 줄 버튼(펼치기·복사·지우기)은 맨 앞에. 끝에 두면 표가 넓어 화면 밖으로 숨는다. */}
                <Th className="w-[7.5rem]" />
                <Th className="w-[10rem]">노선명</Th>
                <Th className="w-[16rem]">출발지</Th>
                <Th className="w-[15.5rem]">출발 일시</Th>
                <Th className="w-[16rem]">도착지</Th>
                <Th className="w-[15.5rem]">도착 일시</Th>
                <Th className="w-[8.5rem]">차종</Th>
                <Th className="w-[7.5rem]" title="기사앱 입찰은 이 해 수 이내(연식 등록된) 차량만 신청됩니다">연식 제한</Th>
                <Th className="w-[4.5rem] text-right">대수</Th>
                <Th className="w-[8.5rem] text-right">청구 총액(노선)</Th>
                <Th className="w-[8.5rem] text-right">운행료(1대당)</Th>
                <Th className="w-[6.5rem]">정산월</Th>
                <Th className="w-[8rem] text-right">운행료 합계</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const total = lineTotal(r.costDriver, r.carCnt);
                const cnt = r.carCnt ?? 1;
                return (
                  <Fragment key={r._key}>
                    <tr className="border-t border-line align-top">
                      <Td className="pt-3 text-xs text-ink-faint">{i + 1}</Td>
                      <Td className="pt-2">
                        <div className="flex gap-1">
                          <IconBtn
                            title={r._open ? '접기' : '왕복·식사·숙박·참고사항 더 보기'}
                            onClick={() => setRow(r._key, { _open: !r._open })}
                          >
                            {r._open ? '▲' : '▼'}
                          </IconBtn>
                          <IconBtn title="이 줄을 바로 아래에 복사합니다(#178)" onClick={() => dupRow(r._key)}>
                            복사
                          </IconBtn>
                          <IconBtn
                            title={rows.length === 1 ? '마지막 줄은 지울 수 없습니다' : '이 줄을 지웁니다'}
                            disabled={rows.length === 1}
                            onClick={() => delRow(r._key)}
                          >
                            ✕
                          </IconBtn>
                        </div>
                        {/* #399 — 왕복은 ▼ 안에만 있어 못 찾았다 → 줄에서 바로 체크(켜면 복편 칸이 보이게 펼침). */}
                        <label className="mt-1 flex cursor-pointer select-none items-center gap-1 text-xs font-medium text-brand-700">
                          <input
                            type="checkbox"
                            checked={r.isShuttle === true}
                            onChange={(e) =>
                              setRow(
                                r._key,
                                e.target.checked
                                  ? { ...(moveReturnToShuttle(r) ?? { isShuttle: true }), _open: true }
                                  : { isShuttle: false },
                              )
                            }
                          />
                          왕복
                        </label>
                      </Td>
                      <Td>
                        <input
                          className="field"
                          placeholder="예: 1일차 서울→속초"
                          value={r.disptName ?? ''}
                          onChange={(e) => setRow(r._key, { disptName: e.target.value })}
                        />
                      </Td>
                      <Td>
                        <input
                          className="field"
                          list="recv-intake-start-locs"
                          value={r.startLoc ?? ''}
                          onChange={(e) => setRow(r._key, { startLoc: e.target.value })}
                        />
                      </Td>
                      <Td>
                        <DepartInput value={r.startTime} onChange={(v) => setStart(r._key, v)} />
                      </Td>
                      <Td>
                        <input
                          className="field"
                          value={r.endLoc ?? ''}
                          onChange={(e) => setRow(r._key, { endLoc: e.target.value })}
                        />
                      </Td>
                      <Td>
                        <ArrivalInput value={r.endTime} onChange={(v) => setRow(r._key, { endTime: v })} />
                      </Td>
                      <Td>
                        <CarKindSelect
                          value={r.carKind}
                          showSettings={false}
                          onChange={(v) => setRow(r._key, { carKind: v })}
                        />
                      </Td>
                      <Td>
                        <CarYearSelect value={r.carYear} onChange={(v) => setRow(r._key, { carYear: v })} />
                      </Td>
                      <Td>
                        <input
                          type="number"
                          min={1}
                          className="field text-right"
                          value={cnt}
                          onChange={(e) => setRow(r._key, { carCnt: Number(e.target.value), _drvTotal: undefined })}
                        />
                      </Td>
                      <Td>
                        <MoneyInput value={r.costCust} onChange={(v) => setRow(r._key, { costCust: v })} />
                      </Td>
                      <Td>
                        <MoneyInput value={r.costDriver} onChange={(v) => setRow(r._key, { costDriver: v, _drvTotal: undefined })} />
                      </Td>
                      <Td>
                        <input
                          className="field"
                          placeholder="YYYY-MM"
                          value={r.driveMon ?? ''}
                          onChange={(e) => setRow(r._key, { driveMon: e.target.value, _monTouched: true })}
                        />
                      </Td>
                      {/* #512 — 운행료 합계도 직접 고친다(합계 ÷ 대수 = 1대당 단가로 저장). */}
                      <Td className="text-right tabular-nums">
                        <MoneyInput
                          aria-label="운행료 합계"
                          value={r._drvTotal ?? (total === null ? '' : String(total))}
                          onChange={(v) => setRow(r._key, { _drvTotal: v, costDriver: unitFromTotal(v, cnt) })}
                        />
                        {total !== null && (cnt > 1 || (r._drvTotal !== undefined && r._drvTotal !== '' && Number(r._drvTotal) !== total)) && (
                          <div className={`mt-0.5 text-xs ${r._drvTotal !== undefined && r._drvTotal !== '' && Number(r._drvTotal) !== total ? 'text-amber-700' : 'text-ink-faint'}`}>
                            {money(r.costDriver)} × {cnt}대 = {money(total)}
                          </div>
                        )}
                      </Td>
                    </tr>

                    {r._open && (
                      <tr className="border-t border-line bg-canvas">
                        <Td />
                        <td colSpan={13} className="px-2 py-3">
                          <div className="flex flex-wrap items-end gap-5">
                            <label className="flex cursor-pointer select-none items-center gap-2 text-sm font-medium">
                              <input
                                type="checkbox"
                                checked={r.isShuttle === true}
                                onChange={(e) =>
                                  setRow(r._key, e.target.checked ? (moveReturnToShuttle(r) ?? { isShuttle: true }) : { isShuttle: false })
                                }
                              />
                              왕복 운행
                            </label>

                            {r.isShuttle && (
                              <>
                                <SubField label="복편 출발 일시">
                                  <ArrivalInput value={r.endDeptTime} onChange={(v) => setRow(r._key, {
                                    endDeptTime: v,
                                    startArrvTime: followDate(r.endDeptTime, v, r.startArrvTime) ?? r.startArrvTime,
                                  })} />
                                </SubField>
                                <SubField label="복편 도착 일시">
                                  <ArrivalInput value={r.startArrvTime} onChange={(v) => setRow(r._key, { startArrvTime: v })} />
                                </SubField>
                              </>
                            )}

                            <label className="flex cursor-pointer select-none items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={r.isMeal === true}
                                onChange={(e) => setRow(r._key, { isMeal: e.target.checked })}
                              />
                              식사 포함
                            </label>
                            <label className="flex cursor-pointer select-none items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={r.isLodge === true}
                                onChange={(e) =>
                                  setRow(r._key, { isLodge: e.target.checked, ...(e.target.checked ? moveReturnToShuttle(r) : null) })
                                }
                              />
                              숙박 포함
                            </label>
                            {/* #275 노선 참고사항 — 입찰 때 기사앱에, 노선 상세에 보인다. */}
                            <input className="field min-w-[24rem] flex-1" maxLength={500} placeholder="참고사항 (입찰 때 기사님께 보입니다)"
                                   value={r.request ?? ''} onChange={(e) => setRow(r._key, { request: e.target.value })} />
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
            <tfoot className="border-t-2 border-line bg-canvas">
              <tr>
                <td colSpan={8} className="px-2 py-2 text-right text-sm font-medium text-ink-soft">
                  청구 합계 · 운행료 합계
                </td>
                {/* 칸 맞춤: 청구 합계는 「청구 총액」 칸 아래, 운행료 합계는 「운행료 합계」 칸 아래. */}
                <td className="px-2 py-2 text-right text-sm font-bold tabular-nums">
                  {money(totals.cust)}
                  {totals.cust !== 0 && (() => {
                    const v = vatSplit(String(totals.cust), vatIncl);
                    return v && (
                      <div className="text-xs font-normal text-ink-faint">
                        {vatIncl ? `공급가 ${money(v.supply)}` : `포함 ${money(v.total)}`}
                      </div>
                    );
                  })()}
                </td>
                <td colSpan={2} />
                <td className="px-2 py-2 text-right text-sm tabular-nums text-ink-soft">{money(totals.driver)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
          <datalist id="recv-intake-start-locs">{startLocs.map((l) => <option key={l} value={l} />)}</datalist>
        </div>

        <p className="mt-2 text-xs text-ink-faint">
          정산월은 출발 일시에서 자동으로 따라옵니다(직접 고치면 그 줄은 더 이상 따라오지 않습니다). 마감된 달이 한 줄이라도
          있으면 접수째로 저장되지 않습니다.
        </p>
      </div>
    </div>
  );
}

function Th({ children, className = '', title }: { children?: React.ReactNode; className?: string; title?: string }) {
  return <th className={`px-2 py-2 text-left font-medium ${className}`} title={title}>{children}</th>;
}

function Td({ children, className = '' }: { children?: React.ReactNode; className?: string }) {
  return <td className={`px-2 py-1.5 ${className}`}>{children}</td>;
}

function IconBtn({
  children,
  title,
  disabled,
  onClick,
}: {
  children: React.ReactNode;
  title: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className="rounded px-1.5 py-1 text-xs text-ink-soft hover:bg-canvas disabled:opacity-30"
    >
      {children}
    </button>
  );
}

function SubField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-ink-soft">{label}</span>
      {children}
    </label>
  );
}
